import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

import Cache from './service/Cache';

// Main Components
import Configurator from './components/Configurator';
import Intro from './components/Intro';
import Final from './components/Final';
import LoadingSpinner from './components/LoadingSpinner';

import './scss/animations.scss';

function App() {
  let appHeight = 0;

  const cache = useMemo(() => new Cache(), []);

  const [state, setState] = useState({
    key: 'intro',
    roomKey: null,
    selectedAccessories: [],
  });

  const [roomTypes, setRoomTypes] = useState(null);
  const [accessories, setAccessories] = useState(null);

  // Refs
  const appRef = useRef();

  const convertToArray = (obj) => {
    return Object.entries(obj).map(([key, value]) => {
      return { key, ...value };
    });
  };

  const getData = useCallback(() => {
    fetch('./locales/i18n/translation.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error getting data - status: ${response.status}`);
        }
        return response.json();
      })
      .then((json) => {
        if (json.rooms) {
          setRoomTypes(convertToArray(json.rooms));
        }
        if (json.accessories) {
          setAccessories(json.accessories);
        }
      });
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    const adobeAnalyticsUrl = process.env.REACT_APP_ADOBE_ANALYTICS_URL;

    if (undefined !== adobeAnalyticsUrl) {
      // Add Adobe Analytics
      const adobeAnalyticsScript = document.createElement('script');
      adobeAnalyticsScript.src = adobeAnalyticsUrl;
      adobeAnalyticsScript.onload = () => {
        // Append to bottom once Adobe has loaded
        const bottomScript = document.createElement('script');
        bottomScript.text = `_satellite.pageBottom();`;
        document.body.appendChild(bottomScript);
      };
      document.body.appendChild(adobeAnalyticsScript);
    }
  }, []);

  const updateHeight = () => {
    appHeight = appRef?.current?.scrollHeight;

    window.parent.postMessage(
      {
        name: 'height',
        height: appHeight,
      },
      '*'
    );
  };

  // Update app height only when height has changed.
  useEffect(() => {
    setInterval(() => {
      if (appHeight === appRef?.current?.scrollHeight) return;

      updateHeight();
    }, 100);
  });

  useEffect(() => {
    // Sync state with cache
    const storedAccessories = cache.get('education-selectedAccessories');

    if (
      (state.selectedAccessories.length > 0 || storedAccessories) &&
      state.key === 'intro'
    ) {
      setState((prevState) => ({
        ...prevState,
        key: 'config',
        roomKey: 'connected',
        selectedAccessories: storedAccessories,
      }));
    }
  }, [state.selectedAccessories.length, state.key, cache]);

  if (!accessories || !roomTypes) return <LoadingSpinner />;

  return (
    <div id='app' ref={appRef}>
      {state.key === 'intro' ? (
        <Intro state={state} setState={setState} roomTypes={roomTypes} />
      ) : null}
      {state.key === 'config' ? (
        <Configurator
          state={state}
          setState={setState}
          roomTypes={roomTypes}
          accessories={accessories}
        />
      ) : null}
      {state.key === 'final' ? (
        <Final
          state={state}
          setState={setState}
          roomTypes={roomTypes}
          accessories={accessories}
        />
      ) : null}
    </div>
  );
}

export default App;
